import React, { useCallback, useEffect, useState } from "react";

import api from "../../services/api";
import AdminLogoHeader from "../../components/admin/AdminLogoHeader";
import { downloadBlob } from "../../utils/download";

const PendingTransactions = () => {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState({});
  const [boRows, setBoRows] = useState([]);
  const [boNotes, setBoNotes] = useState({});
  const [boLoading, setBoLoading] = useState(false);
  const [boError, setBoError] = useState("");
  const [boSaving, setBoSaving] = useState({});
  const [exportingWallet, setExportingWallet] = useState(false);
  const [exportingBackoffice, setExportingBackoffice] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await api.adminListPendingWalletTransactions();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const initialNotes = {};
      list.forEach((row) => {
        initialNotes[row.id] = row.admin_note || "";
      });
      setNotes(initialNotes);
    } catch (err) {
      console.error("pending transactions", err);
      setError(err.message || "Unable to load pending transactions.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBackoffice = useCallback(async () => {
    try {
      setBoLoading(true);
      setBoError("");
      const data = await api.adminListBackofficePostings({ status: "pending" });
      const list = Array.isArray(data.items) ? data.items : [];
      setBoRows(list);
      const initial = {};
      list.forEach((item) => {
        initial[item.id] = item.reconciliation_note || "";
      });
      setBoNotes(initial);
    } catch (err) {
      console.error("pending backoffice postings", err);
      setBoError(err.message || "Unable to load pending postings.");
    } finally {
      setBoLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadBackoffice();
  }, [load, loadBackoffice]);

  const updateRowState = (updater) => {
    setRows((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return typeof updater === "function" ? updater(current) : updater;
    });
  };

  const handleApprove = async (row) => {
    try {
      setSaving((prev) => ({ ...prev, [row.id]: true }));
      await api.adminApprovePendingWalletTransaction(row.id, { note: notes[row.id] });
      updateRowState((list) => list.filter((item) => item.id !== row.id));
      setNotes((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (err) {
      alert(err.message || "Failed to approve transaction.");
    } finally {
      setSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleReject = async (row) => {
    const confirmed = window.confirm(`Reject this ${row.transaction_type.toLowerCase()} request?`);
    if (!confirmed) return;
    try {
      setSaving((prev) => ({ ...prev, [row.id]: true }));
      await api.adminRejectPendingWalletTransaction(row.id, { note: notes[row.id] });
      updateRowState((list) => list.filter((item) => item.id !== row.id));
      setNotes((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (err) {
      alert(err.message || "Failed to reject transaction.");
    } finally {
      setSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleApprovePosting = async (row) => {
    try {
      setBoSaving((prev) => ({ ...prev, [row.id]: true }));
      await api.adminApproveBackofficePosting(row.id, {
        approval_note: boNotes[row.id],
      });
      setBoRows((prev) => prev.filter((item) => item.id !== row.id));
      setBoNotes((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (err) {
      alert(err.message || "Failed to approve posting.");
    } finally {
      setBoSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleRejectPosting = async (row) => {
    const confirmed = window.confirm(`Reject ${row.category_label}?`);
    if (!confirmed) return;
    try {
      setBoSaving((prev) => ({ ...prev, [row.id]: true }));
      await api.adminRejectBackofficePosting(row.id, { reason: boNotes[row.id] });
      setBoRows((prev) => prev.filter((item) => item.id !== row.id));
      setBoNotes((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (err) {
      alert(err.message || "Failed to reject posting.");
    } finally {
      setBoSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleWalletExport = async () => {
    try {
      setExportingWallet(true);
      const blob = await api.adminExportPendingWalletTransactions();
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 13);
      downloadBlob(blob, `pending-wallet-${stamp}.xlsx`);
    } catch (err) {
      alert(err.message || "Unable to export wallet queue.");
    } finally {
      setExportingWallet(false);
    }
  };

  const handleBackofficeExport = async () => {
    try {
      setExportingBackoffice(true);
      const blob = await api.adminExportBackofficePostings({ status: "pending" });
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 13);
      downloadBlob(blob, `pending-postings-${stamp}.xlsx`);
    } catch (err) {
      alert(err.message || "Unable to export pending postings.");
    } finally {
      setExportingBackoffice(false);
    }
  };

  const formatAmount = (value) => `NGN ${Number(value || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="max-w-7xl mx-auto space-y-6 p-6">
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Pending wallet transactions</h1>
              <p className="text-sm text-slate-500">
                Review deposit and withdrawal requests submitted by users before they hit the ledger.
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>{rows.length} pending</span>
              <button
                type="button"
                onClick={handleWalletExport}
                disabled={exportingWallet}
                className="rounded-lg border border-emerald-200 px-3 py-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                {exportingWallet ? "Preparing..." : "Export Excel"}
              </button>
            </div>
          </div>

          {loading ? (
            <p className="px-6 py-6 text-sm text-slate-500">Loading pending transactions...</p>
          ) : error ? (
            <p className="px-6 py-6 text-sm text-red-600">{error}</p>
          ) : !rows.length ? (
            <p className="px-6 py-6 text-sm text-slate-500">No pending transactions.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">User</th>
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    <th className="px-4 py-3 text-left font-medium">Amount</th>
                    <th className="px-4 py-3 text-left font-medium">Wallet balance</th>
                    <th className="px-4 py-3 text-left font-medium">User note</th>
                    <th className="px-4 py-3 text-left font-medium">Created</th>
                    <th className="px-4 py-3 text-left font-medium">Admin note</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => {
                    const isSaving = saving[row.id];
                    const isWithdrawal = row.transaction_type?.toLowerCase() === "withdraw";
                    const hasSufficientBalance = Number(row.wallet_balance || 0) >= Number(row.amount || 0);
                    const isFixedIncome = row.workflow_kind === "investment_purchase";
                    const isLiquidation = row.workflow_kind === "investment_liquidation";
                    const displayName = [row.full_name, row.user_contact].filter(Boolean).join(" - ");
                    return (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-4 py-4 text-slate-700">
                          <p className="font-medium">{displayName || row.user_contact || "-"}</p>
                          {row.user_role && (
                            <p className="text-xs text-slate-500 uppercase">{row.user_role}</p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <p className="capitalize">{row.transaction_type}</p>
                          {isFixedIncome ? (
                            <p className="text-xs font-medium text-indigo-600">Fixed-income funding</p>
                          ) : null}
                          {isLiquidation ? (
                            <p className="text-xs font-medium text-indigo-600">Investment liquidation</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-4 text-slate-700">{formatAmount(row.amount)}</td>
                        <td className="px-4 py-4 text-slate-700">
                          <p>{formatAmount(row.wallet_balance)}</p>
                          {isWithdrawal && !hasSufficientBalance ? (
                            <p className="text-xs font-medium text-red-600">Insufficient funds</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-4 text-slate-600">{row.note || "-"}</td>
                        <td className="px-4 py-4 text-slate-500">
                          {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
                        </td>
                        <td className="px-4 py-4">
                          <textarea
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            rows={3}
                            value={notes[row.id] ?? ""}
                            onChange={(event) => setNotes((prev) => ({ ...prev, [row.id]: event.target.value }))}
                            placeholder="Optional admin note"
                          />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleReject(row)}
                              disabled={isSaving}
                              className="rounded-lg border border-red-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              Decline
                            </button>
                            <button
                              type="button"
                              onClick={() => handleApprove(row)}
                              disabled={isSaving || (isWithdrawal && !hasSufficientBalance)}
                              className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Approve
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Pending back-office postings</h2>
              <p className="text-sm text-slate-500">
                These were submitted from the back-office form and need finance approval before
                hitting the ledger.
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>{boRows.length} pending</span>
              <button
                type="button"
                onClick={handleBackofficeExport}
                disabled={exportingBackoffice}
                className="rounded-lg border border-emerald-200 px-3 py-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                {exportingBackoffice ? "Preparing..." : "Export Excel"}
              </button>
            </div>
          </div>

          {boLoading ? (
            <p className="px-6 py-6 text-sm text-slate-500">Loading pending postings...</p>
          ) : boError ? (
            <p className="px-6 py-6 text-sm text-red-600">{boError}</p>
          ) : !boRows.length ? (
            <p className="px-6 py-6 text-sm text-slate-500">No pending postings.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Client</th>
                    <th className="px-4 py-3 text-left font-medium">Category</th>
                    <th className="px-4 py-3 text-left font-medium">Amount</th>
                    <th className="px-4 py-3 text-left font-medium">Bank ref</th>
                    <th className="px-4 py-3 text-left font-medium">Transaction date</th>
                    <th className="px-4 py-3 text-left font-medium">Evidence</th>
                    <th className="px-4 py-3 text-left font-medium">Reviewer note</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {boRows.map((row) => {
                    const isSaving = boSaving[row.id];
                    return (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-4 py-4 text-slate-700">
                          <p className="font-medium">{row.user?.full_name || row.user?.username || "-"}</p>
                          <p className="text-xs text-slate-500 uppercase">{row.client_account_type}</p>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <p className="font-medium">{row.category_label}</p>
                          <p className="text-xs text-slate-500">
                            {row.direction === "inflow" ? "Inflow" : "Outflow"}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-slate-700">{formatAmount(row.amount)}</td>
                        <td className="px-4 py-4 text-slate-600">{row.bank_reference || "-"}</td>
                        <td className="px-4 py-4 text-slate-500">
                          {row.value_date ? new Date(row.value_date).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          {row.attachment_url ? (
                            <a
                              href={row.attachment_url}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:border-emerald-200 hover:text-emerald-700"
                            >
                              View
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <textarea
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            rows={3}
                            value={boNotes[row.id] ?? ""}
                            onChange={(event) =>
                              setBoNotes((prev) => ({ ...prev, [row.id]: event.target.value }))
                            }
                            placeholder="Approval / rejection note"
                          />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleRejectPosting(row)}
                              disabled={isSaving}
                              className="rounded-lg border border-red-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              onClick={() => handleApprovePosting(row)}
                              disabled={isSaving}
                              className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Approve
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PendingTransactions;
